import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import Search from "./Search";

// Parse markdown content
function parsePost(raw, filename) {
  const lines = raw.split("\n");
  let title = "";
  let category = "";
  let date = "";
  let tags = [];

  // Extract title (first heading)
  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith("# ")) {
      title = t.replace(/^#\s+/, "").trim();
      break;
    }
  }

  // Extract metadata
  const metaMatchCategory = raw.match(/\*\*Category:\*\*\s*(.*)/i);
  const metaMatchDate = raw.match(/\*\*Date:\*\*\s*(.*)/i);
  const metaMatchTags = raw.match(/\*\*Tags:\*\*\s*(.*)/i);
  if (metaMatchCategory) category = metaMatchCategory[1].trim();
  if (metaMatchDate) date = metaMatchDate[1].trim();
  if (metaMatchTags) {
    // Parse tags from format like "#tag1 #tag2 #tag3" or "tag1, tag2, tag3"
    const tagString = metaMatchTags[1].trim();
    if (tagString.includes('#')) {
      // Parse hashtag format
      tags = tagString.match(/#[\w-]+/g)?.map(tag => tag.slice(1)) || [];
    } else if (tagString.includes(',')) {
      // Parse comma-separated format
      tags = tagString.split(',').map(tag => tag.trim()).filter(Boolean);
    } else {
      // Single tag or space-separated
      tags = tagString.split(/\s+/).filter(Boolean);
    }
  }

  // Get body content (skip title and metadata)
  const bodyStartIndex = (() => {
    let idx = 0;
    if (lines[0] && lines[0].trim().startsWith("# ")) idx = 1;
    while (idx < lines.length && (lines[idx].trim() === "" || lines[idx].trim().startsWith("**"))) idx++;
    return idx;
  })();

  const bodyLines = lines.slice(bodyStartIndex);
  const body = bodyLines.join("\n");

  // Create excerpt
  const paragraphs = body
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(p =>
      p.replace(/\*\*Category:\*\*.*$/im, '').replace(/\*\*Date:\*\*.*$/im, '').replace(/\*\*Tags:\*\*.*$/im, '').trim()
    );

  let excerpt = paragraphs.length ? paragraphs[0] : "";
  if (excerpt.length > 220) excerpt = excerpt.slice(0, 220).trim() + "…";

  return {
    id: filename.replace(/\.md$/i, ''),
    title: title || filename,
    category,
    date,
    tags,
    excerpt,
    body,
  };
}

// Simple slugify function
function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

// Deterministic token generator (djb2 -> base36) to avoid exposing filenames in the URL
function generateToken(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  // >>> 0 to force unsigned, then base36 for compact token
  return (h >>> 0).toString(36).slice(-8);
}

// Extract headings from markdown
function extractHeadings(markdown) {
  const lines = markdown.split('\n');
  const headings = [];
  const usedIds = new Set();
  
  for (const line of lines) {
    const match = line.match(/^(#{1,4})\s+(.*)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      let id = slugify(text);
      
      // Make sure ID is unique
      let counter = 1;
      let finalId = id;
      while (usedIds.has(finalId)) {
        finalId = `${id}-${counter}`;
        counter++;
      }
      usedIds.add(finalId);
      
      headings.push({ id: finalId, level, text });
    }
  }
  return headings;
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(new Set());

  // Build a nested tree from flat headings list
  function buildTOCTree(headings) {
    const root = [];
    const stack = [];

    for (const h of headings) {
      const node = { ...h, children: [] };

      while (stack.length && stack[stack.length - 1].level >= node.level) {
        stack.pop();
      }

      if (stack.length === 0) {
        root.push(node);
      } else {
        stack[stack.length - 1].children.push(node);
      }

      stack.push(node);
    }

    return root;
  }

  function toggleCollapse(id) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Load posts on mount
  useEffect(() => {
    const modules = import.meta.glob("../content/posts/*.md", { eager: true, as: "raw" });
    const files = Object.keys(modules)
      .map((path) => {
        const name = path.split("/").pop();
        return parsePost(modules[path], name);
      })
      .sort((a, b) => (a.id < b.id ? 1 : -1))
      // attach deterministic opaque token to each post (stable across loads)
      .map((f) => ({ ...f, token: generateToken(f.id) }));

    setPosts(files);
    setFilteredPosts(files); // Initialize filtered posts with all posts

    // If the user navigated directly to /blog/:token (or legacy filename), open that post
    try {
      const path = window.location.pathname || '';
      const match = path.match(/^\/blog\/(.+)$/);
      if (match) {
        const param = match[1];
        const found = files.find((f) => f.token === param || f.id === param);
        if (found) {
          // Defer to let state settle
          setTimeout(() => openPost(found), 20);
        }
      }
    } catch (e) {
      // ignore (non-browser)
    }
  }, []);

  // Keep browser navigation in sync (back/forward)
  useEffect(() => {
    function handlePop() {
      try {
        const path = window.location.pathname || '';
        const match = path.match(/^\/blog\/(.+)$/);
        if (match) {
          const param = match[1];
          const found = posts.find((p) => p.token === param || p.id === param);
          if (found) {
            // Only open if not already showing
            if (!selectedPost || selectedPost.id !== found.id) {
              openPost(found);
            }
            return;
          }
        }
        // If path is /blog or anything else, ensure list is shown
        if (path === '/blog' || path === '/' || !path.startsWith('/blog/')) {
          if (selectedPost) closePost();
        }
      } catch (e) {
        // ignore
      }
    }

    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [posts, selectedPost]);

  // If the user clicks the site-wide "Blog" nav link while a post is open,
  // some browsers/pages may only scroll to top instead of showing the list.
  // Intercept clicks on anchors pointing to `/blog` and call closePost to
  // ensure the list view is shown in this single-page setup.
  useEffect(() => {
    function onDocumentClick(e) {
      try {
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a || !a.href) return;
        const url = new URL(a.href, window.location.origin);
        if (url.pathname === '/blog') {
          if (selectedPost) {
            e.preventDefault();
            // closePost will clear selectedPost and update history to /blog
            closePost();
          }
        }
      } catch (err) {
        // ignore malformed hrefs or other errors
      }
    }

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [selectedPost]);

  // Open a post
  function openPost(post) {
    const postHeadings = extractHeadings(post.body);
    setHeadings(postHeadings);
    setSelectedPost(post);
    setActiveId(postHeadings[0]?.id || "");
    
    // Scroll to top after a brief delay
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowBackToTop(false);
    }, 100);
  }

  // Close post and go back to list
  function closePost() {
    setSelectedPost(null);
    setHeadings([]);
    setActiveId("");
    setShowBackToTop(false);

    // Update address bar to the blog listing (replace current /blog/:id)
    try {
      const path = window.location.pathname || '';
      if (path.startsWith('/blog/')) {
        window.history.replaceState(null, '', '/blog');
      }
    } catch (e) {
      // ignore
    }
    
    // Scroll to top when going back to blog list
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }

  // Scroll to a specific heading
  function scrollToHeading(headingId) {
    const element = document.getElementById(headingId);
    if (element) {
      const header = document.querySelector('.sticky');
      const offset = header ? header.offsetHeight + 20 : 20;
      const elementTop = element.offsetTop - offset;
      
      window.scrollTo({ top: elementTop, behavior: 'smooth' });
      
      setActiveId(headingId);
      setTimeout(() => setActiveId(headingId), 800);
    }
  }

  // Scroll to top of page
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveId("");
    setShowBackToTop(false);
  }

  // Track which heading is currently visible
  useEffect(() => {
    if (!selectedPost || headings.length === 0) return;

    function updateActiveHeading() {
      const header = document.querySelector('.sticky');
      const offset = header ? header.offsetHeight + 100 : 100;
      
      let foundActive = false;
      
      for (let i = headings.length - 1; i >= 0; i--) {
        const element = document.getElementById(headings[i].id);
        if (element && element.offsetTop <= window.scrollY + offset) {
          setActiveId(headings[i].id);
          foundActive = true;
          break;
        }
      }
      
      if (window.scrollY < 100) {
        setActiveId("");
        setShowBackToTop(false);
      } else {
        setShowBackToTop(true);
      }
    }

    let timeoutId;
    function debouncedUpdate() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateActiveHeading, 50);
    }

    window.addEventListener('scroll', debouncedUpdate);
    return () => {
      window.removeEventListener('scroll', debouncedUpdate);
      clearTimeout(timeoutId);
    };
  }, [selectedPost, headings]);

  // Memoize ReactMarkdown components and rendered output to avoid unnecessary remounts
  const markdownComponents = useMemo(() => ({
    h1: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h1 id={id} className="text-3xl font-bold text-white mt-8 mb-4" {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h2 id={id} className="text-2xl font-bold text-white mt-6 mb-3" {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h3 id={id} className="text-xl font-bold text-white mt-5 mb-2" {...props}>{children}</h3>;
    },
    h4: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h4 id={id} className="text-lg font-bold text-white mt-4 mb-2" {...props}>{children}</h4>;
    },
    h5: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h5 id={id} className="text-base font-bold text-white mt-3 mb-2" {...props}>{children}</h5>;
    },
    h6: ({ children, ...props }) => {
      const text = Array.isArray(children) ? children.join('') : String(children);
      const id = slugify(text);
      return <h6 id={id} className="text-sm font-bold text-gray-200 mt-3 mb-2 uppercase tracking-wide" {...props}>{children}</h6>;
    },
    strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
    em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
    p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-4">{children}</p>,
    li: ({ children }) => <li className="text-gray-300 mb-1">{children}</li>,
    ul: ({ children }) => <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-1">{children}</ol>,
    hr: () => <hr className="border-gray-600 my-8" />,
    table: ({ children }) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full bg-gray-800/50 border border-gray-700 rounded-lg">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2 bg-gray-700 text-left text-white font-semibold border-b border-gray-600">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2 text-gray-300 border-b border-gray-700">
        {children}
      </td>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-purple-500 pl-6 my-6 italic text-gray-300 bg-gray-800/30 py-4 rounded-r-lg">
        {children}
      </blockquote>
    ),
    img: ({ src, alt, ...props }) => (
      <figure className="my-8">
        <img 
          src={src} 
          alt={alt} 
          className="w-full h-auto rounded-lg shadow-lg mx-auto"
          loading="lazy"
          {...props}
        />
        {alt && (
          <figcaption className="text-center text-sm text-gray-400 mt-2 italic">
            {alt}
          </figcaption>
        )}
      </figure>
    ),
    svg: ({ children, ...props }) => (
      <div className="flex justify-center my-6">
        <svg className="max-w-full h-auto" {...props}>
          {children}
        </svg>
      </div>
    ),
    audio: ({ src, children, ...props }) => (
      <div className="my-8 bg-gray-800/50 p-6 rounded-lg border border-gray-700">
        <audio 
          controls 
          className="w-full"
          preload="none"
          {...props}
        >
          {children}
        </audio>
      </div>
    ),
    video: ({ src, children, ...props }) => (
      <div className="my-8">
        <video 
          controls 
          className="w-full h-auto rounded-lg shadow-lg mx-auto"
          preload="none"
          {...props}
        >
          {children}
        </video>
      </div>
    ),
    iframe: ({ src, title, width, height, ...props }) => (
      <div className="my-8">
        <div className="relative w-full overflow-hidden rounded-lg shadow-lg bg-gray-800/50">
          {/* Responsive iframe wrapper */}
          <div className="relative w-full" style={{ paddingBottom: height && width ? `${(height/width) * 100}%` : '56.25%' }}>
            <iframe
              src={src}
              title={title}
              className="absolute top-0 left-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              {...props}
            />
          </div>
        </div>
        {title && (
          <p className="text-center text-sm text-gray-400 mt-2 italic">
            {title}
          </p>
        )}
      </div>
    ),
    code: ({ inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline ? (
        <div className="my-6 bg-gray-800/80 rounded-lg overflow-hidden border border-gray-700">
          <div className="bg-gray-700/50 px-4 py-2 text-xs text-gray-300 font-medium">
            {match ? match[1] : 'code'}
          </div>
          <pre className="p-4 overflow-auto">
            <code className={`text-gray-200 ${className || ''}`} {...props}>
              {children}
            </code>
          </pre>
        </div>
      ) : (
        <code className="bg-gray-800 text-purple-300 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => <>{children}</>,
    div: ({ className, children, ...props }) => {
      // Handle special div classes for enhanced styling
      if (className?.includes('my-8') || className?.includes('bg-gray-800')) {
        return <div className={className} {...props}>{children}</div>;
      }
      return <div className="mb-4" {...props}>{children}</div>;
    },
    span: ({ className, children, ...props }) => (
      <span className={className} {...props}>{children}</span>
    ),
    a: ({ href, children, ...props }) => (
      <a 
        href={href} 
        className="text-purple-400 hover:text-purple-300 underline decoration-purple-400/50 hover:decoration-purple-300 transition-colors"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    ),
  }), []);

  const renderedMarkdown = useMemo(() => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={markdownComponents}
    >
      {selectedPost ? selectedPost.body : ''}
    </ReactMarkdown>
  ), [selectedPost?.body, markdownComponents]);

  // Show individual post
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-900">
        {/* Sticky header */}
        <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-10">
          <div className="container mx-auto px-6 py-4">
            <button
              onClick={closePost}
              className="inline-flex items-center text-purple-400 hover:text-purple-300 font-medium"
            >
              ← Back to Blog
            </button>
          </div>
        </div>

        <div className="container mx-auto px-6 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Table of Contents */}
              <nav className="lg:w-64 lg:shrink-0 lg:sticky lg:top-24 lg:h-fit">
                <div className="bg-gray-900/40 p-4 rounded-xl border border-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <h4
                      className={`text-base font-semibold cursor-pointer transition-colors transform transition-transform duration-150 hover:scale-105 ${
                        showBackToTop ? 'text-purple-300' : 'text-gray-300'
                      }`}
                      onClick={scrollToTop}
                      title="Back to top"
                    >
                      On this page
                    </h4>
                  {/* Toggle button for dropdown/collapse */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setTocOpen((s) => !s); }}
                    aria-expanded={tocOpen}
                    aria-label={tocOpen ? 'Collapse table of contents' : 'Expand table of contents'}
                    className="text-gray-400 hover:text-purple-300 p-1 rounded focus:outline-none"
                    title={tocOpen ? 'Collapse' : 'Expand'}
                  >
                    <svg className={`w-4 h-4 transform transition-transform duration-200 ${tocOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                { /* Render nested TOC with per-heading collapse */ }
                <div className={`${tocOpen ? 'block' : 'hidden'}`}>
                  {headings.length === 0 ? (
                    <p className="text-gray-500">No sections</p>
                  ) : (
                    (() => {
                      const tree = buildTOCTree(headings);

                      function renderNodes(nodes) {
                        return (
                          <ul className="space-y-2 text-sm">
                            {nodes.map((node) => {
                              const hasChildren = node.children && node.children.length > 0;
                              const isCollapsed = collapsed.has(node.id);
                              const isActive = activeId === node.id;
                              const indent = node.level === 1 ? 'pl-0' : node.level === 2 ? 'pl-3' : node.level === 3 ? 'pl-6' : 'pl-9';

                              return (
                                <li key={node.id} className={indent}>
                                  <div className="flex items-center gap-2">
                                    {hasChildren ? (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleCollapse(node.id); }}
                                        aria-expanded={!isCollapsed}
                                        className="text-gray-400 hover:text-purple-300 p-1 rounded focus:outline-none"
                                        title={isCollapsed ? 'Expand' : 'Collapse'}
                                      >
                                        <svg className={`w-3 h-3 transform transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`} viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M6 4l8 6-8 6V4z" />
                                        </svg>
                                      </button>
                                    ) : (
                                      <span className="w-3 h-3 inline-block" />
                                    )}

                                    <button
                                      onClick={() => scrollToHeading(node.id)}
                                      className={`text-left w-full transition-colors transform transition-transform duration-150 ease-out hover:scale-105 ${
                                        isActive ? 'text-purple-300 font-semibold' : 'text-gray-300 hover:text-purple-300'
                                      }`}
                                    >
                                      {node.text}
                                    </button>
                                  </div>

                                  {hasChildren && !isCollapsed && (
                                    <div className="mt-1">
                                      {renderNodes(node.children)}
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        );
                      }

                      return renderNodes(tree);
                    })()
                  )}
                </div>
               </div>
             </nav>

            {/* Article content */}
            <article className="prose prose-lg prose-invert flex-1 min-w-0">
              <div className="max-w-none lg:max-w-4xl">
                <header className="mb-8">
                  <p className="text-sm text-purple-400 font-semibold mb-2">{selectedPost.category}</p>
                  <h1 
                    onClick={scrollToTop}
                    className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 cursor-pointer hover:text-purple-100 transition-colors"
                    title="Click to go to top"
                  >
                    {selectedPost.title}
                  </h1>
                  <p className="text-gray-400 mb-3">{selectedPost.date}</p>
                  {selectedPost.tags && selectedPost.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedPost.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="inline-block px-3 py-1 text-xs font-medium bg-gray-700/60 text-gray-300 rounded-full border border-gray-600 hover:border-purple-500/50 transition-colors"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </header>

                <div className="prose prose-lg prose-invert max-w-none">
                {renderedMarkdown}
               </div>

              {/* Back button at bottom */}
              <div className="mt-12 pt-8 border-t border-gray-800">
                <button
                  onClick={closePost}
                  className="inline-flex items-center font-semibold py-3 px-6 rounded-xl text-base bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                >
                  ← Back to Blog
                </button>
              </div>
              </div>
            </article>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Show blog post list
  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">From Our Blog</h1>
                <p className="text-gray-400 text-sm">Insights on AI, technology, and development</p>
              </div>

              {/* Search on the right (responsive) */}
              <div className="w-full sm:w-80">
                <Search posts={posts} onFilteredPosts={setFilteredPosts} className="mb-0" />
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.length === 0 ? (
          posts.length === 0 ? (
            <p className="text-gray-400 col-span-full text-center py-8">No posts yet.</p>
          ) : (
            <div className="col-span-full text-center py-8">
              <p className="text-gray-400 mb-2">No posts match your search criteria.</p>
              <p className="text-gray-500 text-sm">Try adjusting your filters or search terms.</p>
            </div>
          )
        ) : (
          filteredPosts.map((post) => (
            <a
              key={post.id}
              href={`/blog/${post.token}`}
              onClick={(e) => {
                // Allow default for modified clicks (cmd/ctrl/middle/right open in new tab)
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                openPost(post);
                // Update the URL for shareability / history
                try {
                  window.history.pushState(null, '', `/blog/${post.token}`);
                } catch (err) {}
              }}
              className="no-underline"
            >
              <article
                className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-purple-500/50 transition-all duration-200 cursor-pointer transform hover:-translate-y-1 h-full flex flex-col"
              >
                <div className="flex-1">
                  <p className="text-xs text-purple-400 font-semibold mb-3">{post.category}</p>
                  <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-xs text-gray-500 mb-3">{post.date}</p>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={index}
                          className="inline-block px-2 py-1 text-xs font-medium bg-gray-700/40 text-gray-400 rounded border border-gray-600/50"
                        >
                          #{tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span className="inline-block px-2 py-1 text-xs text-gray-500">
                          +{post.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-auto pt-2">
                  <span className="inline-flex items-center font-medium py-2 px-4 rounded-lg text-sm bg-purple-600/90 hover:bg-purple-700 text-white transition-colors">
                    Read More
                  </span>
                  <span className="text-sm text-purple-400">→</span>
                </div>
              </article>
            </a>
          ))
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
